import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { Button, Form, InputGroup } from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import { api } from '../../../auth'
import {
    searchEntries,
    filterSearchEntries,
    recordToSearchEntry,
    type SearchEntry,
    type SearchRecordResult,
} from '../../../config/searchConfig'

type Variant = 'outer' | 'inner'

type GlobalSearchProps = {
    variant: Variant
    onResultSelect?: () => void
    className?: string
    inputClassName?: string
}

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 200

export default function GlobalSearch({
    variant,
    onResultSelect,
    className = '',
    inputClassName = '',
}: GlobalSearchProps) {
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)
    const navigate = useNavigate()
    const axiosPrivate = api

    const [query, setQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [highlightIndex, setHighlightIndex] = useState(0)
    const [recordResults, setRecordResults] = useState<SearchRecordResult[]>([])
    const [recordsLoading, setRecordsLoading] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const searchAbortRef = useRef<AbortController | null>(null)

    const allEntries = useMemo(() => {
        const modules = initData?.available_modules ?? []
        const moduleEntries: SearchEntry[] = modules.map((m: { code: string; name: string }) => ({
            path: `/client/modules/${m.code}`,
            label: m.name,
            category: 'Modules',
        }))
        return [...searchEntries, ...moduleEntries]
    }, [initData?.available_modules])

    const navResults = useMemo(() => {
        if (!query.trim() || query.length < MIN_QUERY_LENGTH) return []
        return filterSearchEntries(allEntries, query)
    }, [allEntries, query])

    const recordEntries = useMemo(
        () => recordResults.map(recordToSearchEntry),
        [recordResults]
    )

    const flatResults = useMemo(
        () => [...navResults, ...recordEntries],
        [navResults, recordEntries]
    )
    const hasResults = flatResults.length > 0
    const openPanel = useCallback(() => {
        if (query.length >= MIN_QUERY_LENGTH) setIsOpen(true)
    }, [query])

    const closePanel = useCallback(() => {
        setIsOpen(false)
        setHighlightIndex(0)
    }, [])

    useEffect(() => {
        if (query.length >= MIN_QUERY_LENGTH) {
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                setIsOpen(true)
                setHighlightIndex(0)
                debounceRef.current = null

                searchAbortRef.current?.abort()
                searchAbortRef.current = new AbortController()
                setRecordsLoading(true)
                axiosPrivate
                    .get<{ data?: { results?: SearchRecordResult[] } }>('/search', {
                        params: { q: query.trim() },
                        signal: searchAbortRef.current.signal,
                    })
                    .then((res) => {
                        setRecordResults(res.data?.data?.results ?? [])
                    })
                    .catch((err) => {
                        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
                            setRecordResults([])
                        }
                    })
                    .finally(() => {
                        setRecordsLoading(false)
                    })
            }, DEBOUNCE_MS)
        } else {
            setIsOpen(false)
            setRecordResults([])
        }
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
            searchAbortRef.current?.abort()
        }
    }, [query, axiosPrivate])

    useEffect(() => {
        if (!isOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            if (
                panelRef.current?.contains(e.target as Node) ||
                inputRef.current?.contains(e.target as Node)
            ) return
            closePanel()
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, closePanel])

    useEffect(() => {
        if (flatResults.length > 0 && highlightIndex >= flatResults.length) {
            setHighlightIndex(Math.max(0, flatResults.length - 1))
        }
    }, [flatResults.length, highlightIndex])

    const selectHighlighted = useCallback(() => {
        if (flatResults.length === 0) return
        const idx = highlightIndex >= flatResults.length ? 0 : highlightIndex
        const entry = flatResults[idx]
        if (entry) {
            navigate(entry.path)
            setQuery('')
            closePanel()
            onResultSelect?.()
        }
    }, [flatResults, highlightIndex, navigate, closePanel, onResultSelect])

    useEffect(() => {
        if (!isOpen) return
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closePanel()
                inputRef.current?.blur()
                return
            }
            if (flatResults.length === 0) return
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setHighlightIndex((i) => (i + 1) % flatResults.length)
                return
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setHighlightIndex((i) => (i - 1 + flatResults.length) % flatResults.length)
                return
            }
            if (e.key === 'Enter') {
                e.preventDefault()
                e.stopPropagation()
                selectHighlighted()
            }
        }
        document.addEventListener('keydown', handleKeyDown, true)
        return () => document.removeEventListener('keydown', handleKeyDown, true)
    }, [isOpen, flatResults, highlightIndex, closePanel, selectHighlighted])

    const handleSelect = (entry: SearchEntry) => {
        navigate(entry.path)
        setQuery('')
        closePanel()
        onResultSelect?.()
    }

    const grouped = useMemo(() => {
        const map = new Map<string, SearchEntry[]>()
        for (const e of flatResults) {
            const list = map.get(e.category) ?? []
            list.push(e)
            map.set(e.category, list)
        }
        return Array.from(map.entries())
    }, [flatResults])

    const themeBg = theme === 'dark' ? 'dark' : 'light'

    return (
        <div className={`global-search global-search--${variant} ${className}`}>
            <Form
                className={variant === 'outer' ? 'd-none d-sm-inline-block form-inline me-auto ms-md-3 my-2 my-md-0 mw-100 navbar-search' : 'w-100'}
                onSubmit={(e) => {
                    e.preventDefault()
                    if (isOpen && flatResults.length > 0) {
                        selectHighlighted()
                    }
                }}
            >
                <InputGroup className="navbar-search-input-group">
                    <Form.Control
                        ref={inputRef}
                        type="search"
                        placeholder="Search pages and records…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={openPanel}
                        className={`bg-${themeBg} border-0 small global-search__input ${inputClassName}`}
                        aria-label="Search"
                        aria-expanded={isOpen}
                        aria-autocomplete="list"
                        aria-controls="global-search-results"
                        id="global-search-input"
                    />
                    <Button
                        variant={themeBg}
                        className="input-group-append"
                        type="submit"
                        aria-label="Search"
                        onClick={(e) => e.preventDefault()}
                    >
                        <FontAwesomeIcon icon={faSearch} size="sm" />
                    </Button>
                </InputGroup>
            </Form>
            {isOpen && (
                <div
                    ref={panelRef}
                    id="global-search-results"
                    className="global-search__results"
                    role="listbox"
                >
                    <div className="global-search__results-inner">
                        {hasResults ? (
                            grouped.map(([category, entries]) => (
                                <div key={category} className="global-search__group">
                                    <div className="global-search__group-title">{category}</div>
                                    {entries.map((entry) => {
                                        const flatIdx = flatResults.findIndex((r) => r === entry)
                                        const isHighlighted = flatIdx === highlightIndex
                                        return (
                                            <Link
                                                key={`${entry.path}-${entry.label}`}
                                                to={entry.path}
                                                className={`global-search__item ${isHighlighted ? 'global-search__item--highlight' : ''}`}
                                                role="option"
                                                aria-selected={isHighlighted}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    handleSelect(entry)
                                                }}
                                            >
                                                <span className="global-search__item-label">{entry.label}</span>
                                                <span className="global-search__item-path">{entry.path}</span>
                                            </Link>
                                        )
                                    })}
                                </div>
                            ))
                        ) : recordsLoading ? (
                            <div className="global-search__empty">Searching…</div>
                        ) : (
                            <div className="global-search__empty">No pages or records match “{query}”.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
